import { Cron } from '@modules/common/types';
import { useIsRealtimeReportingEnabled } from '@modules/subscribe/hooks';
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import {
  LocalizationProvider,
  TimePicker,
  renderTimeViewClock,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { DayName } from 'generated/graphql';
import { useState } from 'react';
import { OutboundCardDaily } from './OutboundCardDaily';
import { OutboundCardMonthly } from './OutboundCardMonthly';
import { OutboundCardRealtimeAlert } from './OutboundCardRealtimeAlert';
import { OutboundCardWeekly } from './OutboundCardWeekly';
import { DAYS, DAYS_EXCLUDING_WEEKENDS } from './constants';
import { getReportingFrequencyFromCron } from './utils';

interface OutboundCardFrequencyDurationV2Props {
  savedCronValue: Cron;
  cronSchedule: Cron | undefined;
  setCronSchedule: (cronValue: Cron) => void;
  shouldShowRealtimeFrequency: boolean;
  realtimeLabel?: string;
  shouldShowRealtimeAlert?: boolean;
}

export const OutboundCardFrequencyDurationV2 = ({
  savedCronValue,
  cronSchedule,
  setCronSchedule,
  shouldShowRealtimeFrequency,
  realtimeLabel = 'Real-time',
  shouldShowRealtimeAlert = true,
}: OutboundCardFrequencyDurationV2Props) => {
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);

  const isRealtimeReportingEnabled = useIsRealtimeReportingEnabled();

  const cronValue = cronSchedule?.valueV2;
  const hourOfDay = cronValue?.hourOfDay || 0;
  const selectedWeeklyFrequencyDay = cronValue?.dayOfWeek?.[0];
  const frequency = getReportingFrequencyFromCron(cronSchedule);

  const toggleWeeklyFrequencyDayStatus = (day: DayName) => {
    setCronSchedule({
      valueV2: {
        hourOfDay,
        dayOfWeek: [day],
      },
    });
  };

  const toggleDailyFrequencyDayStatus = (days: DayName[]) => {
    setCronSchedule({
      valueV2: {
        hourOfDay,
        dayOfWeek: days,
      },
    });
  };

  const handleTimeValue = (newValue: Dayjs | null) => {
    if (!newValue) {
      return;
    }

    setCronSchedule({
      valueV2: {
        ...cronValue,
        hourOfDay: parseInt(newValue.format('H'), 10),
      },
    });
  };

  const onFrequencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const savedCronValueV2 = savedCronValue.valueV2;

    if (value === 'daily') {
      const isValidDailySelection = savedCronValueV2?.dayOfWeek?.length === 1;
      setCronSchedule({
        valueV2: {
          dayOfWeek: isValidDailySelection
            ? DAYS_EXCLUDING_WEEKENDS
            : savedCronValueV2?.dayOfWeek,
          hourOfDay: savedCronValueV2?.hourOfDay || 0,
          dayOfMonth: undefined,
        },
      });
      return;
    }
    if (value === 'weekly') {
      const savedWeeklyDay = savedCronValueV2?.dayOfWeek?.[0];
      setCronSchedule({
        valueV2: {
          hourOfDay: savedCronValueV2?.hourOfDay || 0,
          dayOfWeek: savedWeeklyDay ? [savedWeeklyDay] : [DayName.Mon],
          dayOfMonth: undefined,
        },
      });
      return;
    }
    if (value === 'realtime') {
      setCronSchedule({
        realtime: true,
      });
      return;
    }

    setCronSchedule({
      valueV2: {
        hourOfDay: savedCronValueV2?.hourOfDay || 0,
        dayOfWeek: savedCronValueV2?.dayOfWeek,
        dayOfMonth: savedCronValueV2?.dayOfMonth || { day: 1 },
      },
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl>
        <RadioGroup row onChange={onFrequencyChange} value={frequency}>
          {isRealtimeReportingEnabled && shouldShowRealtimeFrequency && (
            <FormControlLabel
              value="realtime"
              control={<Radio />}
              label={realtimeLabel}
            />
          )}
          <FormControlLabel value="daily" control={<Radio />} label="Daily" />
          <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
          <FormControlLabel
            value="monthly"
            control={<Radio />}
            label="Monthly"
          />
        </RadioGroup>
      </FormControl>
      {frequency === 'realtime' &&
        shouldShowRealtimeFrequency &&
        isRealtimeReportingEnabled &&
        shouldShowRealtimeAlert && <OutboundCardRealtimeAlert />}
      {frequency === 'daily' && (
        <OutboundCardDaily
          shouldIncludeWeekends={cronValue?.dayOfWeek?.length === DAYS.length}
          toggleDailyFrequencyDayStatus={toggleDailyFrequencyDayStatus}
        />
      )}
      {frequency === 'weekly' && (
        <OutboundCardWeekly
          selectedDay={selectedWeeklyFrequencyDay}
          toggleWeeklyFrequencyDayStatus={toggleWeeklyFrequencyDayStatus}
        />
      )}
      {frequency === 'monthly' && (
        <OutboundCardMonthly
          cronSchedule={cronSchedule}
          setCronSchedule={setCronSchedule}
        />
      )}
      {frequency !== 'realtime' && (
        <Stack direction="row" mt={4} alignItems="center">
          <Box mt={1}>
            <Typography variant="body1" color="text.secondary">
              At
            </Typography>
          </Box>
          <Stack pl={6} direction="row" alignItems="center">
            <TimePicker
              open={isTimePickerOpen}
              onOpen={() => setIsTimePickerOpen(true)}
              onClose={() => setIsTimePickerOpen(false)}
              closeOnSelect={false}
              label="Time"
              views={['hours']}
              value={dayjs().hour(hourOfDay).minute(0)}
              onChange={handleTimeValue}
              slotProps={{
                textField: {
                  style: { width: 150, height: 50 },
                  onClick: (_) => setIsTimePickerOpen(true),
                  onKeyDown: (e) => {
                    setIsTimePickerOpen((isOpen) => !isOpen);
                    e.preventDefault();
                  },
                },
              }}
              viewRenderers={{
                hours: renderTimeViewClock,
              }}
            />
            <Box component="span" pl={1.5}>
              <Typography color="text.secondary">
                {dayjs.tz.guess()} (UTC{dayjs().format('Z')})
              </Typography>
            </Box>
          </Stack>
        </Stack>
      )}
    </LocalizationProvider>
  );
};
